import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Transition } from "@headlessui/react";
import { motion } from "framer-motion";

const CurrentRoundStats = () => {
  const dartCount = useSelector((state) => state.dartGame.dartCount);
  const currentRound = useSelector((state) => state.dartGame.currentRound);
  const roundHistory = useSelector((state) => state.dartGame.roundHistory);
  const minDartRounds = useSelector(
    (state) => state.dartGame.minDartRounds ?? 33
  );
  const totalDartsNeeded = minDartRounds * 3;

  const [isVisible, setIsVisible] = useState(true);

  // Animation for dart count changes
  useEffect(() => {
    setIsVisible(false);
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, [dartCount]);

  // Create placeholder array for empty throws
  const throwPlaceholders = Array(3).fill(null);

  return (
    <div className=" rounded-2xl max-w-xl mx-auto">
      <div className="flex flex-row gap-4">
        {/* Dart Count Card */}
        <motion.div
          className="w-1/3 bg-gradient-to-br from-primary to-primary/80 p-5 rounded-xl shadow-lg border border-primary/20"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <h3 className="text-sm font-semibold uppercase tracking-wider text-test/80">
            Darts
          </h3>
          <div className="mt-3 flex items-center justify-center">
            <Transition
              show={isVisible}
              enter="transition ease-out duration-300"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition ease-in duration-200"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <div className="flex items-baseline">
                <span
                  className={`text-3xl font-bold ${
                    dartCount < totalDartsNeeded ? "text-white" : "text-test"
                  }`}
                >
                  {dartCount}
                </span>
                <span className="text-white text-xl mx-1">/</span>
                <span className="text-test text-xl">{totalDartsNeeded}</span>
              </div>
            </Transition>
          </div>
          <div className="mt-2 h-1 w-full bg-primary/20 rounded-full overflow-hidden">
            <motion.div
              className={`h-full ${
                dartCount < totalDartsNeeded ? "bg-white" : "bg-test"
              }`}
              initial={{ width: 0 }}
              animate={{
                width: `${Math.min(
                  100,
                  (dartCount / totalDartsNeeded) * 100
                )}%`,
              }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </motion.div>

        {/* Last Throws Card */}
        <motion.div
          className="w-2/3 bg-gradient-to-br from-primary to-primary/80 p-5 rounded-xl shadow-lg border border-primary/20"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <h3 className="text-sm font-semibold uppercase tracking-wider text-test/80">
            Last Throws
          </h3>
          <div className="mt-3 min-h-[60px] flex items-center justify-center">
            <div className="grid grid-cols-3 gap-2 w-full">
              {throwPlaceholders.map((_, index) => {
                const hit = currentRound && currentRound[index];
                return (
                  <motion.div
                    key={index}
                    className="flex justify-center"
                    initial={hit ? { opacity: 0, y: 10 } : {}}
                    animate={hit ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div
                      className={`w-[70px] h-[40px] flex items-center justify-center rounded-xl text-lg font-medium
                        ${
                          hit
                            ? "bg-gradient-to-br from-primary/60 to-primary/20 border border-primary/40 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 hover:from-primary/70 hover:to-primary/30"
                            : "bg-primary/20 border border-primary/10 text-white/30"
                        }`}
                    >
                      {hit ? hit.label : "—"}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CurrentRoundStats;
